// Tabela do Clube Fidelidade · Níveis (dados puros — nenhum Vue, nenhuma store).
//
// Fonte: protótipo "Clube Fidelidade · Elos e Recompensas v10" (mobile.html),
// entregue pelo design. São 37 degraus (nível 0 a 36) distribuídos em 14 elos:
// 7 elos abertos (Bronze → Diamante, níveis 0—29) e 7 níveis VIP por convite
// (VIP 0 → VIP 6, níveis 30—36).
//
// O nível 0 não estava no protótipo, mas é um degrau REAL da régua da Smartico
// (`ach_level_current: "NÍVEL 0"`, com id próprio): é onde toda conta nova nasce,
// antes de cruzar os 200 XP do nível 1. Ignorá-lo fazia a tela chamar esse
// jogador de "Nível 1" e deslocava um degrau todos os números de XP da trilha.
//
// O XP real do jogador vem da Smartico (`userInfosProfile.ach_points_ever`);
// tudo daqui pra baixo é só a régua que traduz esse XP em elo, progresso e
// benefícios. Quando o core expuser a tabela, este arquivo vira o fallback.

import { getAssetsUrlForCDN } from '@/utils/template';
import { crmLevelTierKey } from '@/utils/apostatudo/crm-level-slug';

const BADGE_BASE = '/assets/images/levels/badges';
const REWARD_BASE = '/assets/images/levels/rewards';
const PILLAR_BASE = '/assets/images/levels/pillars';

/** Nível a partir do qual o jogador entra na faixa VIP (por convite). */
export const FIRST_VIP_LEVEL = 30;

/**
 * XP acumulado necessário para *estar* no nível N — o índice É o nível
 * (`LEVEL_XP[n]`), com o degrau 0 na frente custando 0 XP.
 *
 * Planilha oficial: níveis 0—29 abertos, 30—36 VIP. A régua da Smartico chega
 * com a mesma forma (`allLevels[n]` = nível n), então as duas são intercambiáveis
 * — é o que permite `xpForLevelIn` funcionar igual nas duas.
 */
export const LEVEL_XP = [
  0, 200, 500, 2000, 5000, 7500, 15000, 30000, 40000, 50000, 60000, 100000,
  150000, 200000, 250000, 300000, 350000, 400000, 425000, 450000, 500000,
  550000, 600000, 650000, 700000, 750000, 800000, 850000, 900000, 950000,
  2000000, 5000000, 10000000, 25000000, 50000000, 100000000, 200000000,
] as const;

/** Último degrau da escala — 36, não 37: o índice é o próprio nível. */
export const MAX_LEVEL = LEVEL_XP.length - 1;

export interface LevelTier {
  key: string;
  name: string;
  /** Primeiro e último nível do elo (inclusivos). */
  from: number;
  to: number;
  /** Cor principal do elo — pinta anel, barra, plaqueta e nebulosa. */
  color: string;
  /** Cor complementar, usada onde o `color` já saturou o bloco. */
  accent: string;
  /** Cashback semanal ao estar neste elo. */
  cashback: string;
  /**
   * O benefício-vitrine do elo, escolhido a dedo pelo produto (não derivado da
   * tabela de benefícios): é o que o cartão de fatos anuncia como "maior
   * benefício" no lugar do cashback.
   */
  topBenefit: string;
  /** VIP 4 pede o benefício-vitrine brilhando — liga o shine no fato. */
  topBenefitShine?: boolean;
  /** VIP não tem tabela: acesso por convite e condições negociadas. */
  isVip: boolean;
  /**
   * Só o degrau "Sem elo" (nível 0) carrega esta flag: ele entra no swiper de
   * elos antes do Bronze, mas não é um elo — não tem fatos, não acende como
   * "você" nos grids e usa título/plaqueta próprios.
   */
  isUnranked?: boolean;
  badge: string;
  /** Arte do pilar usada no pódio do desktop. */
  pillar: string;
  pitch: string;
}

const RAW_TIERS: Omit<LevelTier, 'badge' | 'pillar'>[] = [
  {
    key: 'bronze',
    name: 'Bronze',
    // Começa no 0 de propósito: é o elo de quem acabou de se cadastrar e ainda
    // não pontuou. Com `from: 1` o nível 0 não caía em elo nenhum, e o
    // `findIndex` de `tierIndexOfLevel` devolvia o ÚLTIMO elo — VIP 6.
    from: 0,
    to: 4,
    color: '#FF6A00',
    accent: '#FFC324',
    cashback: 'R$ 10,00',
    topBenefit: 'Freespin',
    isVip: false,
    pitch: 'A porta de entrada, onde toda conta nasce.',
  },
  {
    key: 'prata',
    name: 'Prata',
    from: 5,
    to: 8,
    color: '#8FD0FF',
    accent: '#37D6FF',
    cashback: 'R$ 25,00',
    topBenefit: 'Loja melhorada',
    isVip: false,
    pitch: 'Sua loja fica melhor: itens e descontos que o Bronze não vê.',
  },
  {
    key: 'ouro',
    name: 'Ouro',
    from: 9,
    to: 12,
    color: '#FFC000',
    accent: '#FF7A00',
    cashback: 'R$ 60,00',
    topBenefit: 'Benefício do Clube de Apoiador',
    isVip: false,
    pitch: 'Clube de Apoiador liberado.',
  },
  {
    key: 'safira',
    name: 'Safira',
    from: 13,
    to: 16,
    color: '#0D93FF',
    accent: '#00E58C',
    cashback: 'R$ 140,00',
    topBenefit: 'Promoções exclusivas no site',
    isVip: false,
    pitch:
      'Promoções que só aparecem para quem chegou aqui — no site e no Clube.',
  },
  {
    key: 'ametista',
    name: 'Ametista',
    from: 17,
    to: 20,
    color: '#A32BFF',
    accent: '#FF3DC8',
    cashback: 'R$ 260,00',
    topBenefit: 'Promoções do Clube de Apoiador',
    isVip: false,
    pitch:
      'Meio do caminho para o topo. Tudo que você já tem, com mais volume.',
  },
  {
    key: 'platina',
    name: 'Platina',
    from: 21,
    to: 24,
    color: '#E93EFF',
    accent: '#37D6FF',
    cashback: 'R$ 480,00',
    topBenefit: 'Promoções do Clube de Apoiador',
    isVip: false,
    pitch: 'Penúltimo elo antes do VIP — falta pouco.',
  },
  {
    key: 'diamante',
    name: 'Diamante',
    from: 25,
    to: 29,
    color: '#00E9CB',
    accent: '#9CFF2E',
    cashback: 'R$ 900,00',
    topBenefit: 'Promoções exclusivas no site',
    isVip: false,
    pitch: 'Falta um passo para fazer parte do CLUBE VIP!',
  },
  {
    key: 'v0',
    name: 'VIP 0',
    from: 30,
    to: 30,
    color: '#E8926A',
    accent: '#FFD9C2',
    cashback: 'R$ 1.200,00',
    topBenefit: 'Roleta diária melhorada',
    isVip: true,
    pitch:
      'A entrada do VIP: agora você já tem benefícios muito diferentes dos demais jogadores.',
  },
  {
    key: 'v1',
    name: 'VIP 1',
    from: 31,
    to: 31,
    color: '#C9D4E2',
    accent: '#8FA8C4',
    cashback: 'R$ 1.500,00',
    topBenefit: 'Cashback melhorado em slots e Aviator',
    isVip: true,
    pitch: '0,10% dos jogadores chegam aqui.',
  },
  {
    key: 'v2',
    name: 'VIP 2',
    from: 32,
    to: 32,
    color: '#F0921E',
    accent: '#FFC85A',
    cashback: 'R$ 2.500,00',
    topBenefit: 'Rakeback melhorado',
    isVip: true,
    pitch: 'Cashback melhorado e roleta diária diferenciada.',
  },
  {
    key: 'v3',
    name: 'VIP 3',
    from: 33,
    to: 33,
    color: '#2FA05F',
    accent: '#7BE0A0',
    cashback: 'R$ 4.000,00',
    topBenefit: 'Bônus exclusivos',
    isVip: true,
    pitch:
      'Roleta aprimorada + bônus e promos exclusivas — falta pouco para receber seu gerente VIP.',
  },
  {
    key: 'v4',
    name: 'VIP 4',
    from: 34,
    to: 34,
    color: '#1F6BE0',
    accent: '#6FA8FF',
    cashback: 'R$ 7.000,00',
    topBenefit: 'Gerente pessoal',
    topBenefitShine: true,
    isVip: true,
    pitch:
      'Atendimento com gerente pessoal + bônus sob medida, sem teto de tabela, tudo combinado direto com você.',
  },
  {
    key: 'v5',
    name: 'VIP 5',
    from: 35,
    to: 35,
    color: '#E0142F',
    accent: '#FF6B7E',
    cashback: 'R$ 12.000,00',
    topBenefit: 'Bônus aprimorados',
    isVip: true,
    pitch:
      'Bônus aprimorado com GERENTES VIP + atendimento ainda mais exclusivo.',
  },
  {
    key: 'v6',
    name: 'VIP 6',
    from: 36,
    to: 36,
    color: '#22CFE6',
    accent: '#7BEFFF',
    cashback: 'Negociado',
    topBenefit: 'Melhor atendimento + Bônus exclusivos',
    isVip: true,
    pitch:
      'O topo absoluto. Condições, prêmios e experiências desenhadas só para você.',
  },
];

/**
 * Tabela dos elos com caminhos LOCAIS de arte.
 *
 * O CDN de estáticos NÃO é aplicado aqui de propósito: `getAssetsUrlForCDN` lê
 * `useRuntimeConfig()`, que em tempo de módulo cai no fallback e devolve o
 * caminho local. Quem precisa das artes chama `levelTiers()`, que resolve
 * dentro do contexto Nuxt — mesma razão documentada em `utils/vip-tiers.ts`.
 */
export const LEVEL_TIERS: LevelTier[] = RAW_TIERS.map((tier) => ({
  ...tier,
  badge: `${BADGE_BASE}/${tier.key}.webp`,
  pillar: `${PILLAR_BASE}/${tier.key}.webp`,
}));

export const LEVEL_TIER_COUNT = LEVEL_TIERS.length;

// A trava de nível da loja (antes `STORE_UNLOCK_LEVEL = 3`, ENG-489) saiu daqui:
// o degrau mínimo de cada gamificação é da operação, editado no backoffice do
// core e entregue no /players/me (`gamifications.store.min_level`) — ver
// stores/player-profile.ts e BaseLevelGateNotice.

/**
 * Elo que um slug CRM do core nomeia ('BRONZE LV 1' → o tier `bronze`), pra
 * emblema e cores do aviso de nível. Degrau sem elo (NIVEL 0, slug inesperado,
 * `null`) cai no Bronze — o primeiro elo, e o que a arte antiga da loja sempre
 * mostrou. Artes em caminho LOCAL, como `LEVEL_TIERS` (ver comentário lá).
 */
export function crmLevelTier(slug: unknown): LevelTier | undefined {
  const key = crmLevelTierKey(slug);

  return (
    LEVEL_TIERS.find((tier) => tier.key === key) ??
    LEVEL_TIERS.find((tier) => tier.key === 'bronze')
  );
}

/**
 * "Bronze 3" para o nível 3 — o nome do elo mais a posição dentro dele. No VIP
 * não há subdivisão, então cai no `levelLabel` ("VIP 2").
 */
export function tierLevelLabel(level: number): string {
  if (level >= FIRST_VIP_LEVEL) return levelLabel(level);

  const tier = LEVEL_TIERS[tierIndexOfLevel(level)];

  return `${tier.name} ${level - displayFrom(tier) + 1}`;
}

/** Elos com as artes já passadas pelo CDN. Chame de dentro do contexto Nuxt. */
export function levelTiers(): LevelTier[] {
  return LEVEL_TIERS.map((tier) => ({
    ...tier,
    badge: getAssetsUrlForCDN(tier.badge),
    pillar: getAssetsUrlForCDN(tier.pillar),
  }));
}

/**
 * O degrau "Sem elo" do swiper: onde toda conta nasce (nível 0), antes do
 * Bronze. Não é um elo — não entra em `LEVEL_TIERS` nem nos grids; só o
 * swiper de progresso o mostra, com a arte rachada. Chame no contexto Nuxt.
 */
export function unrankedStep(): LevelTier {
  return {
    key: 'lvl0',
    name: 'Sem elo',
    from: 0,
    to: 0,
    color: LEVEL_ZERO_SKIN.color,
    accent: LEVEL_ZERO_SKIN.accent,
    cashback: '—',
    topBenefit: '—',
    isVip: false,
    isUnranked: true,
    badge: getAssetsUrlForCDN(LEVEL_ZERO_SKIN.badge),
    pillar: getAssetsUrlForCDN(LEVEL_ZERO_SKIN.pillar),
    pitch:
      'Toda conta nasce aqui — alcance o nível 1 para conquistar o Bronze.',
  };
}

/**
 * Nível de atendimento do elo — o protótipo deriva do teto do elo em vez de
 * guardar por elo.
 */
export function supportLevel(tier: LevelTier): string {
  if (tier.to >= 34) return 'Dedicado';

  return tier.to >= 25 ? 'Prioritário' : 'Padrão';
}

/** Caminho local do ícone de prêmio — o CDN entra depois, já no contexto Nuxt. */
const ART = (name: string) => `${REWARD_BASE}/${name}.webp`;

/**
 * Grupos do comparativo de benefícios (desktop). O índice é o `group` de cada
 * benefício.
 */
export const BENEFIT_GROUPS = [
  'Loja e promoções',
  'Retorno em dinheiro',
  'Atendimento VIP',
] as const;

export interface LevelBenefit {
  label: string;
  /** Nível em que o benefício é liberado. */
  from: number;
  /** Índice em `BENEFIT_GROUPS` — agrupa as linhas do comparativo. */
  group: number;
  art: string;
  desc: string;
  /**
   * Benefício que está na tabela mas ainda não é entregue por nenhum elo. O
   * comparativo mostra a linha sem nenhuma marcação — nada de check verde nem
   * herdado — em vez de prometer o que a operação ainda não tem.
   */
  pending?: boolean;
}

/**
 * Os benefícios na ordem agrupada do comparativo — que é a fonte da verdade
 * da tela: o desktop lê esta ordem direto e abre um cabeçalho de grupo a cada
 * troca de `group`, e as listas do mobile derivam daqui. Mexer na ordem ou
 * marcar `pending` aqui reflete em todas elas de uma vez.
 */
export const LEVEL_BENEFITS: LevelBenefit[] = [
  {
    label: 'Freespin',
    from: 1,
    group: 0,
    art: ART('slot-gem'),
    desc: 'Rodadas grátis liberadas conforme as campanhas do seu elo.',
  },
  {
    label: 'Loja melhorada',
    from: 5,
    group: 0,
    art: ART('slot-machine'),
    desc: 'Acesso aos maiores descontos em itens da loja.',
  },
  {
    label: 'Benefício do Clube de Apoiador',
    from: 9,
    group: 0,
    art: ART('mascot-capi'),
    desc: 'Benefícios extras por apoiar seu criador favorito.',
  },
  {
    label: 'Eventos exclusivos',
    from: 9,
    group: 0,
    art: ART('gem'),
    desc: 'Torneios e ações fechadas, abertas só a partir do seu elo.',
  },
  {
    label: 'Promoções exclusivas no site',
    from: 13,
    group: 0,
    art: ART('gem-fire'),
    desc: 'Campanhas que só aparecem para o seu elo.',
  },
  {
    label: 'Promoções do Clube de Apoiador',
    from: 13,
    group: 0,
    art: ART('chest-gems'),
    desc: 'Ofertas combinadas com o criador que você apoia.',
  },
  {
    label: 'Roleta diária melhorada',
    from: 30,
    group: 0,
    art: ART('slot-gem'),
    desc: 'Uma jogada garantida a cada 24h com prêmios maiores.',
  },
  {
    label: 'Cashback melhorado em slots e Aviator',
    from: 30,
    group: 1,
    art: ART('coin-fire'),
    desc: 'Percentual maior de volta em slots e no Aviator.',
  },
  {
    label: 'Cashback em jogos ao vivo',
    from: 30,
    group: 1,
    art: ART('bucket-coins'),
    desc: 'Dinheiro de volta também no cassino ao vivo.',
  },
  {
    label: 'Rakeback melhorado',
    from: 30,
    group: 1,
    art: ART('coin-pile'),
    desc: 'Parte da margem da casa volta para você.',
  },
  {
    label: 'Saque priorizado',
    from: 30,
    group: 1,
    art: ART('coin'),
    desc: 'Limite de saque maior, dentro das regras de saque da conta.',
  },
  {
    label: 'Saldo real',
    from: 30,
    group: 1,
    art: ART('sack-blue'),
    desc: 'Prêmios em saldo real, sem rollover.',
    // Mesmo caso do saldo bônus: a operação ainda não entrega prêmio em saldo
    // real, então a linha fica sem marcação em elo nenhum em vez de prometer.
    pending: true,
  },
  {
    label: 'Saldo bônus',
    from: 9,
    group: 1,
    art: ART('bag-coins'),
    desc: 'Bônus creditado conforme as regras do programa. Pode variar de acordo com a campanha do mês.',
    // Ainda não existe saldo bônus na operação: a linha fica sem marcação em
    // elo nenhum até o benefício existir de verdade. Fica por último no grupo
    // pra não abrir "Retorno em dinheiro" com uma fileira vazia.
    pending: true,
  },
  {
    label: 'Bônus exclusivos com gerente',
    from: 34,
    group: 2,
    art: ART('mascot-urso'),
    desc: 'Ofertas montadas sob medida pelo seu gerente.',
  },
  {
    label: 'Atendimento dedicado',
    from: 34,
    group: 2,
    art: ART('chest-open'),
    desc: 'Fila própria e resposta prioritária a qualquer hora.',
  },
];

export const LEVEL_BENEFIT_COUNT = LEVEL_BENEFITS.length;

/** Benefícios com as artes já passadas pelo CDN. Chame dentro do contexto Nuxt. */
export function levelBenefits(): LevelBenefit[] {
  return LEVEL_BENEFITS.map((benefit) => ({
    ...benefit,
    art: getAssetsUrlForCDN(benefit.art),
  }));
}

/* ------------------------------------------------------------------ *
 * Cálculo de nível / elo
 * ------------------------------------------------------------------ */

/**
 * Nível correspondente a um XP acumulado. Piso 0 (quem não pontuou está no
 * nível 0, não no 1) e teto `MAX_LEVEL`.
 */
export function levelAtXp(xp: number): number {
  let level = 0;

  for (let i = 0; i < LEVEL_XP.length; i++) {
    if (xp >= LEVEL_XP[i]) level = i;
  }

  return Math.min(level, MAX_LEVEL);
}

/**
 * Índice em `LEVEL_TIERS` do elo que contém o nível.
 *
 * O fallback é o PRIMEIRO elo, não o último: fora da faixa quer dizer nível
 * abaixo da escala, e mandar esse jogador pro VIP 6 (o que o `-1` do `findIndex`
 * fazia) pintava a tela inteira com o elo mais alto do jogo.
 */
export function tierIndexOfLevel(level: number): number {
  const index = LEVEL_TIERS.findIndex(
    (tier) => level >= tier.from && level <= tier.to,
  );

  if (index >= 0) return index;

  return level > MAX_LEVEL ? LEVEL_TIERS.length - 1 : 0;
}

/** "Nível 12" para a faixa aberta, "VIP 3" a partir do nível 30. */
export function levelLabel(level: number): string {
  return level < FIRST_VIP_LEVEL
    ? `Nível ${level}`
    : `VIP ${level - FIRST_VIP_LEVEL}`;
}

/**
 * Quais benefícios já estão ativos num nível.
 *
 * Pendente nunca entra: o comparativo é a fonte da verdade e mostra a linha
 * sem marcação em elo nenhum, então contar aqui um benefício que a operação
 * ainda não entrega faria a lista do mobile prometer o que a matriz nega.
 */
export function benefitsAtLevel(level: number): LevelBenefit[] {
  return LEVEL_BENEFITS.filter(
    (benefit) => !benefit.pending && level >= benefit.from,
  );
}

/* ------------------------------------------------------------------ *
 * Formatação
 * ------------------------------------------------------------------ */

export function formatXp(value: number): string {
  return value.toLocaleString('pt-BR');
}

/** 1.800 → "2 mil", 5.000.000 → "5 Milhões". Usado onde não cabe o número cheio. */
export function compactXp(value: number): string {
  if (value >= 1_000_000) {
    const millions = value / 1_000_000;
    const label = millions === 1 ? 'Milhão' : 'Milhões';

    return `${millions.toLocaleString('pt-BR', {
      maximumFractionDigits: 1,
    })} ${label}`;
  }

  // Uma casa decimal, como nos milhões. Arredondar pro inteiro fazia o degrau
  // quebrado MENTIR: os 7.500 que faltavam pro nível 6 apareciam como "8 mil"
  // no cartão do jogador enquanto o contador do jogo dizia 7500 — dois números
  // pra mesma distância. `toLocaleString` derruba o ",0" sozinho, então os
  // degraus redondos continuam saindo como "15 mil".
  if (value >= 1000) {
    return `${(value / 1000).toLocaleString('pt-BR', {
      maximumFractionDigits: 1,
    })} mil`;
  }

  return String(value);
}

/**
 * 1 casa decimal pra porcentagens de progresso de nível/elo (barra de XP do
 * header, cartão do jogador, comparativo de elos, faixa do jogo). Arredondar
 * pra inteiro mentia: com vãos de XP grandes (ex.: 17,1 mil/25 milhões), um
 * progresso real de 0,06% aparecia como "0%", indistinguível de progresso
 * zerado de verdade — mesmo raciocínio do `compactXp` acima, aplicado à
 * porcentagem em vez do número de XP.
 */
export function roundProgressPercent(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Quantos caracteres o par de números pode ter antes de precisar encurtar.
 * Não é gosto: o contador da faixa mede ~4px por caractere (8,03px de Plus
 * Jakarta itálico) e, num celular de 360 com os três chips na placa, sobram
 * ~110px pra ele. Vinte caracteres é o que cabe com folga de um.
 */
const XP_PAIR_BUDGET = 20;

/**
 * "XP atual / XP do próximo nível" do contador da faixa de nível no header do
 * jogo (Figma 22799:60743). Sem `next` (nível máximo, onde "X / X" não diria
 * nada) sai só o XP acumulado.
 *
 * Os números saem CRUS como no desenho — separador de milhar custaria ~18% de
 * largura, justo onde não há. Quando nem crus caberiam, os DOIS lados perdem os
 * mesmos 3 zeros e ganham um "k": encurtar só um deles destruiria a razão entre
 * eles, que é a única coisa que o contador está dizendo.
 *
 * O corte é sempre de mil em mil e só entra quando o maior número já passou do
 * milhão — assim nenhum lado desaba pra "0k" por causa do arredondamento.
 */
export function xpPairLabel(current: number, next?: number | null): string {
  const from = Math.max(0, Math.floor(current || 0));
  const to = next == null ? null : Math.max(0, Math.floor(next));

  const raw = to == null ? `${from}` : `${from} / ${to}`;

  if (raw.length <= XP_PAIR_BUDGET || Math.max(from, to ?? 0) < 1_000_000) {
    return `${raw} XP`;
  }

  const short = (value: number) => `${Math.floor(value / 1000)}k`;

  return to == null ? `${short(from)} XP` : `${short(from)} / ${short(to)} XP`;
}

/* ------------------------------------------------------------------ *
 * Cor — o protótipo deriva todos os tons da cor do elo em runtime.
 * ------------------------------------------------------------------ */

function toRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');

  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

/** `#FF6A00` + alpha → `rgba(255,106,0,0.32)`. */
export function hexAlpha(hex: string, alpha: number): string {
  const [r, g, b] = toRgb(hex);

  return `rgba(${r},${g},${b},${alpha})`;
}

/** Interpola duas cores; `t` = 0 devolve `from`, `t` = 1 devolve `to`. */
export function mixColor(from: string, to: string, t: number): string {
  const a = toRgb(from);
  const b = toRgb(to);

  return `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * t)).join(',')})`;
}

/**
 * Versão "acesa" da cor do elo: sobe a saturação e segura a claridade num teto.
 *
 * Os elos claros (Prata `#8FD0FF`, VIP 1 `#C9D4E2`) são quase brancos. Usados
 * cru em brilho/halo/raio, o resultado é um véu leitoso — a tela toda parece
 * desbotada. Aqui a cor vira tinta: mantém o matiz, ganha pigmento.
 */
export function vividColor(hex: string): string {
  const [r, g, b] = toRgb(hex).map((v) => v / 255);

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;

  let h = 0;
  let s = 0;

  if (d) {
    s = d / (1 - Math.abs(2 * l - 1));

    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;

    h = (((h * 60) % 360) + 360) % 360;
  }

  // Cinza puro não tem matiz pra saturar: pigmentar viraria um tom inventado.
  const s2 = d ? Math.min(1, s * 1.25 + 0.2) : 0;
  const l2 = Math.min(l, 0.54);

  const c = (1 - Math.abs(2 * l2 - 1)) * s2;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l2 - c / 2;

  const rgb = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ][Math.floor(h / 60) % 6];

  return `#${rgb
    .map((v) =>
      Math.round((v + m) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

export interface CardParticle {
  left: string;
  top: string;
  size: string;
  blur: string;
  color: string;
  duration: string;
  delay: string;
}

/**
 * Pontinhos que pulsam DENTRO do cartão do jogador (o `meParticles` do
 * protótipo), atrás do badge fantasma. Não são as fagulhas que sobem no fundo
 * da página — essas vivem nas views.
 *
 * Posição, tamanho e tempo derivam do índice, nunca de `Math.random`: com
 * random cada re-render sorteia tudo de novo e o cartão "pisca".
 */
export function cardParticles(tier: LevelTier): CardParticle[] {
  return Array.from({ length: 18 }, (_, i) => {
    const variant = i % 4;

    return {
      left: `${(((i * 29 + (i % 4) * 13) % 96) + 2).toFixed(1)}%`,
      top: `${(((i * 43 + (i % 3) * 17) % 84) + 8).toFixed(1)}%`,
      size: `${[3.4, 2.6, 2, 1.6][variant]}px`,
      blur: `${[9, 7, 5, 5][variant]}px`,
      color: [
        mixColor(tier.color, '#ffffff', 0.5),
        tier.accent,
        hexAlpha(tier.color, 0.9),
        'rgba(255,255,255,.9)',
      ][variant],
      duration: `${(3.6 + (i % 7) * 0.7).toFixed(1)}s`,
      delay: `${((i * 0.47) % 5.5).toFixed(1)}s`,
    };
  });
}

/** Luminância relativa — decide se o texto sobre a cor sai claro ou escuro. */
export function luminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((v) => v / 255);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** Cor de texto legível sobre um fundo da cor dada. */
export function readableOn(hex: string): string {
  return luminance(hex) > 0.58 ? '#0A0F1E' : '#ffffff';
}

/* ------------------------------------------------------------------ *
 * Comparativo de benefícios (desktop)
 * ------------------------------------------------------------------ */

export interface MatrixColumn {
  name: string;
  badge: string;
  color: string;
  cashback: string;
  from: number;
  to: number;
  range: string;
}

/**
 * Colunas do comparativo: os 7 elos abertos, um por coluna, e as 7 faixas VIP
 * condensadas numa única coluna "VIP" (30–36) — no VIP não há tabela, então
 * abrir sete colunas iguais só faria a matriz estourar a largura.
 */
/**
 * Primeiro nível APRESENTADO pelo elo. O Bronze guarda `from: 0` porque a
 * matemática precisa (toda conta nasce no degrau 0), mas se apresenta como
 * "1–4": o nível 0 é o estado "sem elo" e não pertence à vitrine de ninguém.
 */
export function displayFrom(tier: Pick<LevelTier, 'from'>): number {
  return Math.max(1, tier.from);
}

/**
 * Visual do jogador que ainda está no nível 0: cinza e com o emblema rachado,
 * no lugar da roupa de Bronze — ele ainda não conquistou elo nenhum. Veste por
 * cima do Bronze (spread) para o resto do cartão continuar funcionando.
 */
export const LEVEL_ZERO_SKIN = {
  color: '#8C97A8',
  accent: '#C9D2DF',
  badge: `${BADGE_BASE}/lvl0.webp`,
  pillar: `${PILLAR_BASE}/lvl0.webp`,
} as const;

export function matrixColumns(): MatrixColumn[] {
  const tiers = levelTiers();

  const open = tiers
    .filter((tier) => !tier.isVip)
    .map((tier) => ({
      name: tier.name,
      badge: tier.badge,
      color: tier.color,
      cashback: tier.cashback,
      from: tier.from,
      to: tier.to,
      // O Bronze COMEÇA no 0 na matemática (toda conta nasce lá), mas se
      // APRESENTA como 1–4: o nível 0 é o estado "sem elo" (cinza, emblema
      // rachado) e não entra na vitrine de nenhum elo.
      range: `${displayFrom(tier)}–${tier.to}`,
    }));

  const vips = tiers.filter((tier) => tier.isVip);

  return [
    ...open,
    {
      name: 'VIP',
      badge: vips[0].badge,
      color: '#F0C060',
      cashback: 'sob medida',
      from: vips[0].from,
      to: vips[vips.length - 1].to,
      range: `${vips[0].from}–${vips[vips.length - 1].to}`,
    },
  ];
}

/* ------------------------------------------------------------------ *
 * Dúvidas frequentes (desktop)
 * ------------------------------------------------------------------ */

export const FAQ_CATEGORIES = [
  { key: 'xp', label: 'Ganhar XP' },
  { key: 'niveis', label: 'Níveis e elos' },
  { key: 'premios', label: 'Prêmios' },
  { key: 'vip', label: 'VIP' },
  { key: 'geral', label: 'Informações gerais' },
] as const;

export interface FaqItem {
  category: string;
  question: string;
  answer: string;
}

/**
 * As dúvidas frequentes da tela de níveis — textos oficiais do produto
 * (revisão 08/2026). Tudo estático: nenhuma resposta depende do elo nem do XP
 * do jogador, então a função não recebe parâmetros.
 */
export function buildLevelFaq(): FaqItem[] {
  return [
    {
      category: 'xp',
      question: 'XP em Slots',
      answer:
        '10 XP por R$ 1,00 apostado, ganhando ou perdendo. Ex.: R$ 100,00 apostado → 1.000 XP.',
    },
    {
      category: 'xp',
      question: 'XP no Cassino ao vivo',
      answer:
        '5 XP por R$ 1,00 (roleta ao vivo, blackjack, bacará). Ex.: R$ 100,00 → 500 XP.',
    },
    {
      category: 'xp',
      question: 'XP em Crash / Aviator',
      answer:
        '10 XP por R$ 1,00 de ganho com multiplicadores acima de 1,5x (o que você retirou, não o que apostou). Ex.: apostou R$ 100,00 e retirou R$ 500,00 → 5.000 XP.',
    },
    {
      category: 'xp',
      question: 'Existe aposta mínima para pontuar XP?',
      answer:
        'Sim, apostas maiores que R$ 0,10. O XP começa a contar a partir de R$ 1,00 apostado (acumulado) no total, e apostas menores vão somando: duas apostas — ou, no caso do crash, ganhos — de R$ 0,50, por exemplo, já fecham R$ 1,00 e pontuam normalmente.',
    },
    {
      category: 'xp',
      question: 'Apostas feitas com saldo bônus contam XP?',
      answer:
        'Não. Apostas e ganhos feitos com saldo bônus não geram XP em nenhuma hipótese — só o valor apostado com saldo real acumula pontos.',
    },
    {
      category: 'niveis',
      question: 'Como subo de nível?',
      answer:
        'Acumulando XP. Cada nível tem uma meta; ao alcançá-la, você sobe na hora. A cada poucos níveis você também troca de elo — de Bronze para Prata, e assim por diante —, o que libera prêmios e benefícios novos. Seu progresso e quanto falta para o próximo nível aparecem no topo desta página.',
    },
    {
      category: 'niveis',
      question: 'Quantos níveis e elos existem?',
      answer:
        'São 37 níveis, do 0 ao 36, agrupados em 14 elos — Bronze, Prata, Ouro, Safira, Ametista, Platina e Diamante — e sete faixas VIP acima deles. Toda conta nova começa no nível 0.',
    },
    {
      category: 'niveis',
      question: 'Posso perder meu elo?',
      answer:
        'Não. Elo conquistado é permanente — você nunca desce de faixa. E os benefícios dos elos anteriores seguem valendo conforme você sobe: cada elo novo soma, não substitui.',
    },
    {
      category: 'niveis',
      question: 'Quando o nível é atualizado?',
      answer:
        'Na hora. Assim que seu XP cruza a marca do próximo nível, a subida é automática e os novos benefícios ficam disponíveis na mesma sessão — sem recarregar a página nem solicitar nada.',
    },
    {
      category: 'premios',
      question: 'Preciso resgatar os prêmios?',
      answer:
        'Depende do prêmio. Os benefícios de elo entram automaticamente quando você sobe. Já os prêmios que dependem de uma escolha sua ficam com o botão Resgatar ativo — é só clicar para receber na aba de prêmios e recompensas. https://apostatudo.bet.br/user/rewards',
    },
    {
      category: 'premios',
      question: 'Prêmios resgatados expiram?',
      answer:
        'Bônus e rodadas grátis valem por 1 dia a partir do resgate — use dentro do prazo para não perder. Os benefícios permanentes do elo não expiram.',
    },
    {
      category: 'premios',
      question: 'Os prêmios acumulam entre elos?',
      answer:
        'Sim. Tudo que você já liberou continua valendo: cada elo novo soma benefícios aos anteriores, nunca substitui.',
    },
    {
      category: 'premios',
      question: 'O que é a Loja e como funciona?',
      answer:
        'É onde você troca as coins que acumula apostando por itens — de rodadas grátis a funcionalidades extras. E ela acompanha o seu elo: quanto mais alto, melhor fica a Loja, com mais itens e melhores condições de troca.',
    },
    {
      category: 'vip',
      question: 'Como entro no VIP?',
      answer:
        'As faixas VIP começam no nível 30. Ao chegar lá, o convite é automático.',
    },
    {
      category: 'vip',
      question: 'O que muda no VIP?',
      answer:
        'Bastante coisa. Você ganha gerente dedicado a partir do VIP 4, saques com prioridade (na frente da fila), cashback negociado caso a caso, presentes exclusivos e personalizados.',
    },
    {
      category: 'vip',
      question: 'O gerente atende em qualquer horário?',
      answer:
        'A partir do VIP 4 o atendimento passa a ser dedicado em horário comercial.',
    },
    {
      category: 'vip',
      question: 'Posso perder o status VIP se parar de jogar?',
      answer:
        'Não. O status VIP é permanente e não cai por inatividade — se você der uma pausa, volta com o mesmo nível e os mesmos benefícios.',
    },
    {
      category: 'geral',
      question: 'Preciso me inscrever no clube?',
      answer:
        'Não precisa. Toda conta entra no clube automaticamente, desde o primeiro acesso — sem cadastro e sem taxa.',
    },
    {
      category: 'geral',
      question: 'Onde acompanho meu progresso?',
      answer:
        'Aqui mesmo, nesta página: você vê seu XP acumulado, quanto falta para o próximo nível e o que cada elo libera, tudo em tempo real.',
    },
    {
      category: 'geral',
      question: 'Preciso pedir o cashback?',
      answer:
        'Não precisa se preocupar com isso. O crédito cai automaticamente direto na sua conta, sem abrir solicitação nem falar com o suporte.',
    },
  ];
}

/* ------------------------------------------------------------------ *
 * Fronteiras de XP
 * ------------------------------------------------------------------ */

/**
 * XP acumulado necessário para *estar* no nível `n`, numa régua qualquer.
 *
 * O índice é o próprio nível (`table[n]`), com o degrau 0 na posição 0. Fora da
 * faixa, prende no primeiro/último degrau.
 *
 * Existe como função pura, e não repetida em cada componente, porque a convenção
 * do índice já rendeu dois off-by-one: primeiro o `n - 2`, depois o `n - 1` que
 * sobrou quando a régua ganhou o nível 0 na frente.
 */
export function xpForLevelIn(table: number[], n: number): number {
  if (!table.length) return 0;

  if (n <= 0) return table[0];

  return table[Math.min(n, table.length - 1)];
}

/**
 * XP em que o jogador *entra* no elo e em que *sai* dele.
 *
 * Sair do elo é alcançar o nível seguinte ao seu teto (`to + 1`) — é a
 * convenção do protótipo desktop. O mobile usava o XP do próprio `to`, o que
 * fazia a barra do elo fechar 100% ainda dentro dele.
 */
export function tierXpBounds(
  table: number[],
  tier: Pick<LevelTier, 'from' | 'to'>,
): { entry: number; exit: number } {
  return {
    entry: xpForLevelIn(table, tier.from),
    exit: xpForLevelIn(table, tier.to + 1),
  };
}
